// src/components/HorariosMultiSede.tsx
//
// El bloque "Horarios" de la landing, cuando el profesional atiende en más de
// una sede.
//
// EL PROBLEMA QUE RESUELVE
// -----------------------
// El bloque original muestra el horario de UNA sola sede (la que esté
// seleccionada, que al entrar es la default). Con varias sedes eso miente por
// omisión: la página de Adolfo Gallo decía "Lunes: No disponible" mientras
// tenía la agenda abierta de 08:00 a 20:00 los lunes en Resistencia, y
// "Sábado: No disponible" con un sábado de 10 horas cargado en Charata.
// Quien necesitaba un lunes leía que no atiende y se iba.
//
// Acá se muestran los días de TODAS las sedes activas, con el lugar al lado.
//
// POR QUÉ LA CIUDAD VA SIEMPRE
// ----------------------------
// Las sedes pueden estar a 20 km (Corrientes–Resistencia) o a 250 (Charata).
// No se intenta adivinar qué le queda cerca al paciente: se le dice dónde es
// y decide.
//
// LAS FECHAS ESPECIALES SON DINÁMICAS
// -----------------------------------
// Una sede puede no tener horario semanal y atender solo fechas puntuales.
// Esa sección aparece ÚNICAMENTE si hay fechas futuras cargadas. Un cartel
// fijo que diga "atiende en fechas puntuales" es peor que nada: plantea una
// pregunta que no responde y obliga al paciente a escribir para averiguar.

import React, { useEffect, useState } from 'react';
import { MapPin, CalendarDays } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/** Cuántas fechas especiales se listan antes de resumir el resto. */
const TOPE_FECHAS = 3;

interface Sede {
  id: number;
  name: string;
  city?: string;
  is_active?: boolean;
}

interface Props {
  professionalId: number;
  sedes: Sede[];
  /** Límite de anticipación del profesional, en días. */
  maxAdvanceBooking?: number;
  /**
   * Arranca mostrando SOLO los días que atiende, con un botón para ver la
   * semana entera. Se usa en el celular, donde este bloque compite con el
   * botón de reservar: los "Sin atención" ocupan la mitad del alto para no
   * decir nada, y empujan la acción principal abajo del pliegue.
   */
  compacto?: boolean;
}

/** Una franja de atención de una sede en un día de la semana. */
interface Franja {
  sede: string;
  ciudad?: string;
  desde: string;
  hasta: string;
  cortaDesde?: string;
  cortaHasta?: string;
}

interface FechaEspecial {
  fecha: string; // YYYY-MM-DD
  sede: string;
  ciudad?: string;
  desde?: string;
  hasta?: string;
}

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

/** Hoy en Argentina. El servidor corre en UTC y de noche adelanta un día. */
const hoyArgentina = (): string =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const sumarDias = (iso: string, dias: number): string => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
};

const formatoLargo = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
};

/** "09:30" -> "9:30": así se dice y se lee un horario ("de 9:30 a 12:30"). */
const hora = (h: string): string => h.replace(/^0(\d)/, '$1');

const soloFecha = (v: unknown): string => {
  if (typeof v === 'string') return v.split('T')[0];
  try {
    return new Date(v as string).toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const HorariosMultiSede: React.FC<Props> = ({
  professionalId,
  sedes,
  maxAdvanceBooking = 60,
  compacto = false,
}) => {
  const [cargando, setCargando] = useState(true);
  const [semana, setSemana] = useState<Franja[][]>(() => DIAS.map(() => []));
  const [especiales, setEspeciales] = useState<FechaEspecial[]>([]);
  const [verSemanaCompleta, setVerSemanaCompleta] = useState(false);

  useEffect(() => {
    let vigente = true;

    const cargar = async () => {
      if (!professionalId) {
        setCargando(false);
        return;
      }
      const activas = sedes.filter((s) => s.is_active !== false);

      // SIN SEDES CARGADAS SE PREGUNTA EL HORARIO GENERAL.
      //
      // Antes, con cero sedes esto cortaba y devolvía la semana vacía: la
      // landing decía "Sin atención" los siete días a gente que sí atiende.
      // Le pasaba a 9 de los 20 profesionales con horarios cargados, que es
      // casi la mitad, y es lo peor que puede decir una página de turnos.
      //
      // El endpoint sin `locationId` devuelve el horario general, que es
      // justo lo que corresponde cuando no hay sedes.
      const fuentes =
        activas.length > 0
          ? activas.map((s) => ({ sede: s, query: `?locationId=${s.id}` }))
          : [{ sede: null as Sede | null, query: '' }];

      // Dos pedidos por fuente, todos en paralelo. Para tres sedes son seis:
      // se nota menos que el primer render de la página.
      const resultados = await Promise.all(
        fuentes.map(async ({ sede, query }) => {
          const base = `${API_URL}/api/schedule/professional/${professionalId}`;
          const [cfg, exc] = await Promise.all([
            fetch(`${base}/config/public${query}`).then((r) => r.json()).catch(() => null),
            fetch(`${base}/exceptions/public${query}`).then((r) => r.json()).catch(() => null),
          ]);
          return { sede, cfg, exc };
        })
      );

      if (!vigente) return;

      const nuevaSemana: Franja[][] = DIAS.map(() => []);
      const nuevasFechas: FechaEspecial[] = [];

      const desde = hoyArgentina();
      const hasta = sumarDias(desde, maxAdvanceBooking);

      for (const { sede, cfg, exc } of resultados) {
        // Una sede en "solo días especiales" tiene el horario semanal APAGADO
        // aunque siga cargado: el motor de turnos no lo mira. Antes se
        // mostraba igual, y la página de Gallo anunciaba "Lunes 17:30–20:00"
        // en Freschi, un horario que nadie podía reservar.
        const soloEspeciales = !!cfg?.scheduleConfig?.onlySpecialDays;
        const dias = cfg?.scheduleConfig?.workingDays;
        if (Array.isArray(dias) && !soloEspeciales) {
          dias.forEach((d: any, indice: number) => {
            if (!d?.isWorking) return;
            nuevaSemana[indice].push({
              sede: sede?.name || '',
              ciudad: sede?.city,
              desde: d.startTime,
              hasta: d.endTime,
              ...(d.hasBreak ? { cortaDesde: d.breakStart, cortaHasta: d.breakEnd } : {}),
            });
          });
        }

        // Solo las excepciones que ABREN el día (is_working). Las que cierran
        // son bloqueos —feriados, vacaciones— y no se anuncian acá.
        const lista = exc?.exceptions;
        if (Array.isArray(lista)) {
          for (const e of lista) {
            if (!e?.is_working) continue;
            const fecha = soloFecha(e.exception_date);
            // Pasadas no, y tampoco más allá de donde el paciente puede
            // reservar: anunciar una fecha que no se puede tomar es peor
            // que no anunciarla.
            if (!fecha || fecha < desde || fecha > hasta) continue;
            nuevasFechas.push({
              fecha,
              sede: sede?.name || '',
              ciudad: sede?.city,
              desde: e.custom_start_time ? String(e.custom_start_time).slice(0, 5) : undefined,
              hasta: e.custom_end_time ? String(e.custom_end_time).slice(0, 5) : undefined,
            });
          }
        }
      }

      nuevasFechas.sort((a, b) => a.fecha.localeCompare(b.fecha));

      setSemana(nuevaSemana);
      setEspeciales(nuevasFechas);
      setCargando(false);
    };

    cargar();
    return () => {
      vigente = false;
    };
  }, [professionalId, sedes, maxAdvanceBooking]);

  if (cargando) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-sm text-gray-600 mt-2">Cargando horarios...</p>
      </div>
    );
  }

  const visibles = especiales.slice(0, TOPE_FECHAS);
  const restantes = especiales.length - visibles.length;

  // Una sola sede: repetir su nombre en los siete renglones es ruido. El
  // paciente ya sabe dónde atiende, lo dice el resto de la ficha.
  const mostrarSede = sedes.filter((s) => s.is_active !== false).length > 1;

  const diasCerrados = semana.filter((f) => f.length === 0).length;
  // Sin ningún día semanal pero con fechas especiales, una semana de "Sin
  // atención" no dice nada: se muestran directamente las fechas.
  const soloFechas = diasCerrados === DIAS.length && especiales.length > 0;
  const ocultarCerrados = compacto && !verSemanaCompleta && diasCerrados > 0 && !soloFechas;

  return (
    <div>
      <div className="space-y-3">
        {DIAS.map((nombre, indice) => {
          const franjas = semana[indice];
          if (franjas.length === 0) {
            if (ocultarCerrados || soloFechas) return null;
            return (
              <div key={nombre} className="flex justify-between items-baseline gap-3">
                <span className="text-gray-600">{nombre}</span>
                <span className="text-gray-400 text-sm">Sin atención</span>
              </div>
            );
          }
          return (
            <div key={nombre} className="flex justify-between items-start gap-3">
              <span className="text-gray-600 flex-shrink-0">{nombre}</span>
              <div className="text-right min-w-0">
                {franjas.map((f, i) => (
                  <div key={`${f.sede}-${i}`} className={i > 0 ? 'mt-1.5' : ''}>
                    {/* Con corte, los dos tramos dichos igual: "9:30 a 12:30 y
                        16:30 a 20:30" (oct-2026, Esteban: simetría). Antes era
                        "09:30–20:30 (corta 12:30–16:30)", y había que restar
                        de cabeza para saber a qué hora se podía ir. Cada tramo
                        no se parte: si no entra, baja entero al otro renglón. */}
                    <div className="font-medium text-green-700 text-sm">
                      {f.cortaDesde && f.cortaHasta ? (
                        <>
                          <span className="whitespace-nowrap">{hora(f.desde)} a {hora(f.cortaDesde)}</span>
                          <span className="text-gray-500 font-normal"> y </span>
                          <span className="whitespace-nowrap">{hora(f.cortaHasta)} a {hora(f.hasta)}</span>
                        </>
                      ) : (
                        <span className="whitespace-nowrap">{hora(f.desde)} a {hora(f.hasta)}</span>
                      )}
                    </div>
                    {mostrarSede && (
                      <div className="text-[12px] text-gray-500 flex items-center justify-end gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {f.sede}
                          {f.ciudad ? ` · ${f.ciudad}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {ocultarCerrados && (
        <button
          onClick={() => setVerSemanaCompleta(true)}
          className="mt-3 text-[13px] text-[#0B7C87] font-medium active:opacity-60"
        >
          + ver semana completa
        </button>
      )}

      {/* Solo si hay fechas futuras cargadas. Sin ellas, esta sección no
          existe: un título sin contenido deja al paciente peor que antes. */}
      {visibles.length > 0 && (
        <div className={soloFechas ? '' : 'mt-5 pt-4 border-t border-gray-200'}>
          <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" />
            Próximas fechas especiales
          </h4>
          <div className="space-y-2.5">
            {visibles.map((f) => (
              <div key={`${f.fecha}-${f.sede}`}>
                {/* first-letter y no `capitalize`: `capitalize` pone mayúscula
                    en CADA palabra y deja "Sábado, 3 De Octubre". */}
                <div className="text-sm font-medium text-gray-900 first-letter:uppercase">
                  {formatoLargo(f.fecha)}
                  {f.desde && (
                    <span className="text-green-700 font-medium normal-case"> · {hora(f.desde)} a {hora(f.hasta || '')}</span>
                  )}
                </div>
                <div className="text-[12px] text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">
                    {f.sede}
                    {f.ciudad ? ` · ${f.ciudad}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {restantes > 0 && (
            <p className="text-[12px] text-gray-500 mt-2.5">
              y {restantes} fecha{restantes > 1 ? 's' : ''} más — mirá el calendario al reservar
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HorariosMultiSede;
